import type { Block } from '@pageboss/shared';
import { InspectorSection } from './inspector-section';
import { InspectorField } from './inspector-field';
import { Repeater, RepeaterTitle } from './repeater';
import { ImageUploadField } from './image-upload-field';

type TeamBlock = Extract<Block, { type: 'team' }>;
type TeamProps = TeamBlock['props'];
type Member = TeamProps['members'][number];

function newMember(): Member {
  return {
    name: 'New team member',
    role: '',
    bio: '',
    photo: { url: '', alt: '' },
  };
}

interface TeamInspectorProps {
  block: TeamBlock;
  onChange: (next: TeamProps) => void;
}

export function TeamInspector({ block, onChange }: TeamInspectorProps) {
  const props = block.props;
  const patch = (next: Partial<TeamProps>) => onChange({ ...props, ...next });

  return (
    <>
      <InspectorSection>
        <InspectorField
          label="Title"
          value={props.title ?? ''}
          placeholder="Meet the team"
          onChange={(v) => patch({ title: v })}
        />
      </InspectorSection>

      <InspectorSection label="Members">
        <Repeater
          items={props.members}
          onChange={(members) => patch({ members: members as TeamProps['members'] })}
          addLabel="Add member"
          makeNew={newMember}
          renderTitle={(member) => <RepeaterTitle>{member.name}</RepeaterTitle>}
          renderForm={(member, _i, update) => (
            <>
              <InspectorField
                label="Name"
                value={member.name}
                placeholder="Sam Patel"
                onChange={(v) => update({ name: v })}
              />
              <InspectorField
                label="Role (optional)"
                value={member.role ?? ''}
                placeholder="Owner & lead technician"
                onChange={(v) => update({ role: v })}
              />
              <InspectorField
                label="Bio (optional)"
                value={member.bio ?? ''}
                multiline
                rows={3}
                placeholder="20 years in the trades. Lives in San Diego with two kids and a rescue dog."
                onChange={(v) => update({ bio: v })}
              />
              <ImageUploadField
                label="Photo (optional)"
                url={member.photo?.url ?? ''}
                onChange={(url) =>
                  update({ photo: { url, alt: member.photo?.alt ?? '' } })
                }
              />
              <InspectorField
                label="Photo alt (optional)"
                value={member.photo?.alt ?? ''}
                placeholder={`Photo of ${member.name || 'the team member'}`}
                helperText="Describe the photo for screen readers"
                onChange={(alt) =>
                  update({ photo: { url: member.photo?.url ?? '', alt } })
                }
              />
            </>
          )}
        />
      </InspectorSection>
    </>
  );
}
